import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'
import Cropper from 'cropperjs'

export default class extends Controller {
  static targets = [
    'fileInput',
    'modal',
    'image',
    'resultPreview',
    'resultImage'
  ]

  connect () {
    this.cropper = null
    this.objectUrl = null
    this.previewObjectUrl = null
    this.modalInstance = null
  }

  disconnect () {
    this.#destroyCropper()

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }

    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl)
      this.previewObjectUrl = null
    }

    if (this.modalInstance) {
      this.modalInstance.hide()
      this.modalInstance.dispose()
      this.modalInstance = null
    }
  }

  handleFileSelect (e) {
    const file = e.target.files[0]
    if (!file) return

    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      alert('JPEG もしくは PNG 形式の画像を選択してください。')
      this.resetInput()
      return
    }

    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.objectUrl = URL.createObjectURL(file)

    const img = new Image()
    img.onload = () => {
      this.imageAspectRatio = img.naturalWidth / img.naturalHeight
      this.imageTarget.src = this.objectUrl
      this.#showModal()
    }
    img.src = this.objectUrl
  }

  applyCrop () {
    if (!this.cropper) return
    const size = 512
    const selection = this.cropper.getCropperSelection()

    selection.$toCanvas({
      width: size,
      height: size
    }).then((canvas) => {
      canvas.toBlob((blob) => {
        if (!blob) return
        const croppedFile = new File([blob], this.#generateFileName(), { type: 'image/png' })
        const dt = new DataTransfer()
        dt.items.add(croppedFile)
        this.fileInputTarget.files = dt.files
        if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl)
        this.previewObjectUrl = URL.createObjectURL(blob)
        this.resultImageTarget.src = this.previewObjectUrl
        this.resultPreviewTarget.hidden = false
        this.#hideModal()
      }, 'image/png')
    })
  }

  cancelCrop () {
    this.resetInput()
    this.#hideModal()
  }

  resetInput () {
    this.fileInputTarget.value = ''
    this.resultPreviewTarget.hidden = true
    this.resultImageTarget.src = ''
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl)
      this.previewObjectUrl = null
    }
    this.#destroyCropper()
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
    this.imageAspectRatio = null
  }

  #showModal () {
    if (!this.modalInstance) {
      this.modalInstance = new Modal(this.modalTarget, { backdrop: 'static', keyboard: false })
      this.modalTarget.addEventListener('shown.bs.modal', () => this.#initCropper())
      this.modalTarget.addEventListener('hidden.bs.modal', () => this.#destroyCropper())
    }
    this.modalInstance.show()
  }

  #hideModal () {
    if (this.modalInstance) {
      this.modalInstance.hide()
    }
  }

  #initCropper () {
    this.#destroyCropper()
    const img = this.imageTarget

    // 画像コンテナの幅からアスペクト比に合った高さを算出
    const availableWidth = img.parentElement.clientWidth
    const maxHeight = window.innerHeight * 0.6
    const ratio = this.imageAspectRatio || 1
    const canvasHeight = Math.min(Math.round(availableWidth / ratio), maxHeight)

    this.cropper = new Cropper(img, {
      template: `
        <cropper-canvas background="false" style="height: ${canvasHeight}px;">
          <cropper-image initial-center-size="contain" scalable translatable></cropper-image>
          <cropper-shade hidden></cropper-shade>
          <cropper-handle action="select" plain></cropper-handle>
          <cropper-selection initial-coverage="1" aspect-ratio="1" movable resizable>
            <cropper-grid role="grid" covered></cropper-grid>
            <cropper-crosshair centered></cropper-crosshair>
            <cropper-handle action="move" theme-color="rgba(255,255,255,0)"></cropper-handle>
            <cropper-handle action="n-resize"></cropper-handle>
            <cropper-handle action="e-resize"></cropper-handle>
            <cropper-handle action="s-resize"></cropper-handle>
            <cropper-handle action="w-resize"></cropper-handle>
            <cropper-handle action="ne-resize"></cropper-handle>
            <cropper-handle action="nw-resize"></cropper-handle>
            <cropper-handle action="se-resize"></cropper-handle>
            <cropper-handle action="sw-resize"></cropper-handle>
          </cropper-selection>
        </cropper-canvas>
      `
    })
  }

  #destroyCropper () {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    // destroy() は元の <img> の display を '' に戻すが、
    // 残存する cropper-canvas があれば念のため除去
    const container = this.imageTarget.parentElement
    if (container) {
      container.querySelectorAll('cropper-canvas').forEach((el) => el.remove())
    }
    this.imageTarget.style.display = ''
  }

  #generateFileName () {
    const timeStamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)
    return `icon_${timeStamp}.png`
  }
}
